import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Callout,
  ComparisonTable,
  MarketingPage,
  Section,
} from "@/components/marketing-page";
import { marketingHead, SITE_URL, type FAQ } from "@/lib/seo";

const PATH = "/ai-share-of-voice";
const TITLE = "How to Measure AI Share of Voice: Formula and Example";
const H1 = "How to Measure Your Brand's AI Share of Voice (and Check If You Appear in AI Search)";
const UPDATED_ISO = "2026-10-14";
const DESCRIPTION =
  "How to measure your brand's AI share of voice across ChatGPT, Claude, Gemini, Perplexity and Google AI, with the formula, a worked example and a way to check if you appear in AI search.";
const TERM_DEFINITION =
  "AI Share of Voice (ASOV) is the share of AI-generated answers, across a fixed prompt set and model set, in which a brand appears, weighted by how prominently and how favorably it appears in each answer.";

const FAQS: FAQ[] = [
  {
    q: "How do I measure my brand's AI share of voice?",
    a: "Measure your brand's AI share of voice by asking AI assistants a fixed set of buyer questions several times, scoring each answer that names a brand, and dividing your weighted appearances by the weighted appearances of every brand in those answers, times 100.",
  },
  {
    q: "How do I know if my brand appears in AI search results?",
    a: "You know your brand appears in AI search results by asking ChatGPT, Claude, Gemini, Perplexity and Google AI the questions your buyers ask, without your brand name, and recording whether each answer names or cites you. The free Monroya check at monroya.ai/check does this for you.",
  },
  {
    q: "What is AI Share of Voice (ASOV)?",
    a: "AI Share of Voice is the share of AI-generated answers, across a fixed prompt set and model set, in which a brand appears, weighted by how prominently and how favorably it appears. It answers a different question than a mention count: not only whether an assistant named you, but how much of the answer belonged to you.",
  },
  {
    q: "What is the ASOV formula?",
    a: "ASOV = (sum of your weighted appearances / sum of weighted appearances for every brand in the same answers) x 100. One sampled answer is one prompt, run once, against one assistant, so total samples equal prompts x models x runs per prompt. Hold all three constant between cycles or the numbers are not comparable.",
  },
  {
    q: "How is ASOV different from classic share of voice?",
    a: "Classic share of voice counts impressions or mentions across channels you can largely buy into, and it measures exposure. ASOV counts presence inside answers a model generates on the buyer's behalf, which you influence only through the sources the model trusts. Exposure can be purchased; answer presence has to be earned.",
  },
  {
    q: "How is ASOV different from Share of Model?",
    a: "Share of Model is a yes-or-no frequency metric: the percentage of sampled answers that name you at all. ASOV is competitive and weighted: of everything an assistant said in those answers, how much of it was about you compared with the other brands named alongside you. Share of Model tells you whether you show up; ASOV tells you how much room you take.",
  },
  {
    q: "How many runs are needed for a reliable ASOV?",
    a: "More than one, always. Model output is stochastic, so the same prompt can return a different brand set minutes apart. Sample every prompt several times per cycle and average. Single-run readings produce swings that look like performance changes but are variance.",
  },
  {
    q: "Should ASOV be tracked per assistant or blended?",
    a: "Track it per assistant and report the blend second. ChatGPT, Claude, Gemini, Perplexity, Google AI Overviews and Google AI Mode return materially different brand sets for the same question and update on different schedules, so a blended figure can hold steady while your position collapses on the one assistant your buyers actually use.",
  },
];

const FRAMEWORK = [
  {
    step: "Step 1",
    name: "Frame the question set",
    does: "Build the prompt set from the questions buyers actually ask, grouped by journey stage: Discovery, Evaluation, Decision. Not keywords, and not questions that already name your brand.",
    produces: "A fixed, stage-labelled prompt set that stays constant between measurement cycles.",
    prevents:
      "Measuring a flattering question set. Brand-name prompts inflate every score and hide the Discovery gap, which is where most brands are actually missing.",
  },
  {
    step: "Step 2",
    name: "Sample across models and runs",
    does: "Run every prompt against each assistant several times per cycle, recording the full answer and its cited sources, not just whether your name appeared.",
    produces: "A per-assistant, per-run answer ledger that the rest of the framework scores.",
    prevents:
      "Reporting variance as movement. A single run per prompt swings enough on its own to invent wins and losses that never happened.",
  },
  {
    step: "Step 3",
    name: "Score presence, not just mentions",
    does: "Classify every appearance as recommended, listed, or caveated, then weight it by where it lands in the answer. A brand named first as the recommendation is not equal to a brand in a caveat at the end.",
    produces: "A weighted share figure per assistant per stage, with the competitor set scored the same way.",
    prevents:
      "A rising mention count reading as rising preference, when the added mentions are hedged or buried.",
  },
  {
    step: "Step 4",
    name: "Attribute to sources and act",
    does: "Trace each answer back to the pages and citations behind it, and rank the gaps by how many answers a single fix would touch.",
    produces: "A ranked fix list: the specific pages, questions, and source types to work on next.",
    prevents:
      "Ending with a dashboard number nobody can act on. A share figure without its sources tells you that you lost, not where.",
  },
];

export const Route = createFileRoute("/ai-share-of-voice")({
  head: () => {
    const organization = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "monroya.ai",
      alternateName: ["Monroya AI", "monroya.ai"],
      url: SITE_URL,
      logo: `${SITE_URL}/favicon-512.png`,
      description:
        "Monroya tracks AI visibility across ChatGPT, Claude, Gemini, Perplexity, Google AI Overviews and Google AI Mode, and publishes the AI Share of Voice (ASOV) measurement framework.",
      sameAs: [
        "https://www.facebook.com/Monroyaai/",
        "https://www.instagram.com/monroya_ai/",
        "https://www.linkedin.com/company/monroya-ai",
      ],
      knowsAbout: [
        "AI Share of Voice",
        "AI visibility tracking",
        "Generative engine optimization",
        "Share of Model",
        "Answer engine optimization",
      ],
    };

    const howTo = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "The Monroya ASOV Framework",
      description:
        "Monroya's six-step framework for measuring AI Share of Voice: frame the question set, sample across models and runs, score presence rather than raw mentions, then attribute results back to sources and act.",
      url: `${SITE_URL}${PATH}`,
      creator: { "@type": "Organization", name: "monroya.ai", url: SITE_URL },
      publisher: { "@type": "Organization", name: "monroya.ai", url: SITE_URL },
      about: { "@type": "DefinedTerm", name: "AI Share of Voice (ASOV)" },
      step: FRAMEWORK.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.name,
        text: `${s.does} Produces: ${s.produces}`,
      })),
    };

    return marketingHead({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Glossary", path: "/glossary" },
        { name: "How to measure AI share of voice", path: PATH },
      ],
      faqs: FAQS,
      authorName: "Jeremy Unruh",
      dateModified: UPDATED_ISO,
      definedTerm: {
        name: "AI Share of Voice (ASOV)",
        description: TERM_DEFINITION,
      },
      extraSchemas: [organization, howTo],
    });
  },
  component: Page,
});

function Page() {
  return (
    <MarketingPage
      stamp="Guide"
      title={H1}
      dek="How much of the answer space your brand owns when AI assistants answer your buyers' questions, how to calculate it, and how to check whether you appear at all."
      updated="October 14, 2026"
      faqs={FAQS}
      related={[
        {
          label: "Best AI visibility tools to track how AI recommends you",
          to: "/best-ai-visibility-tools-for-b2b-marketing-teams",
          description: "Six tools compared, with today's pricing",
        },
        {
          label: "What is Share of Model?",
          to: "/what-is-share-of-model",
          description: "The frequency metric ASOV builds on",
        },
        {
          label: "AI visibility vs LLMO",
          to: "/llmo-vs-geo",
          description: "Where these terms sit relative to each other",
        },
        {
          label: "Full glossary",
          to: "/glossary",
          description: "GEO, LLMO, AEO, citation rate, mention class",
        },
      ]}
    >
      <p className="text-[13px] text-muted-foreground">By Jeremy Unruh, Founder of Monroya</p>

      <Callout label="Quick answer">
        <p>
          Measure your brand's AI share of voice by asking AI assistants a fixed set of buyer
          questions several times, scoring each answer that names a brand, and dividing your
          weighted appearances by the weighted appearances of every brand in those answers.
        </p>
      </Callout>

      <Callout label="Definition">
        <p>
          <strong className="text-ink">AI Share of Voice (ASOV)</strong> is the
          share of AI-generated answers, across a fixed prompt set and model
          set, in which a brand appears, weighted by how prominently and how
          favorably it appears in each answer.
        </p>
      </Callout>

      <Section heading="ASOV, share of voice, and Share of Model">
        <p>
          The three terms get used interchangeably and mean different things.
          The distinction that matters is what each one counts and whether you
          can buy your way into it.
        </p>
        <ComparisonTable
          headers={["", "What it counts", "How you move it"]}
          rows={[
            [
              "Classic share of voice",
              "Impressions or mentions across media channels",
              "Largely purchasable: spend, placements, PR",
            ],
            [
              "Share of Model",
              "Percentage of sampled AI answers that name you at all",
              "Earned: be a source the model trusts on that question",
            ],
            [
              "AI Share of Voice (ASOV)",
              "Your weighted presence inside those answers versus every brand named alongside you",
              "Earned and competitive: own more of the answer than rivals do",
            ],
          ]}
        />
        <p>
          Share of Model asks whether you showed up. ASOV asks how much of the
          answer was yours once you did. A brand can hold a steady Share of
          Model while its ASOV falls, because competitors are being named more
          prominently in the same answers.
        </p>
      </Section>

      <Section heading="The formula">
        <Callout label="Formula">
          <p className="text-base">
            ASOV = (your weighted appearances ÷ weighted appearances for all
            brands in the same answers) × 100
          </p>
          <p className="mt-2 text-base">
            total answers sampled = prompts × models × runs per prompt
          </p>
        </Callout>
        <p>
          One sampled answer is one prompt, run once, against one assistant.
          Weighting comes from step 3 of the framework below: a recommendation
          in the opening sentence counts for more than a caveated mention at the
          end. Keep the prompt set, the model set, and the number of runs fixed
          between cycles, or the two figures are not comparable.
        </p>
      </Section>

      <Section heading="The Monroya ASOV Framework">
        <p>
          Four steps, in order. Each one exists because skipping it produces a
          specific, predictable wrong answer.
        </p>
        <div className="space-y-4">
          {FRAMEWORK.map((s) => (
            <div
              key={s.name}
              className="rounded-lg border border-border bg-panel px-5 py-4"
            >
              <div className="text-[13px] text-muted-foreground">
                {s.step}
              </div>
              <h3 className="mt-1 font-serif text-xl text-ink">{s.name}</h3>
              <p className="mt-2 text-ink/85">{s.does}</p>
              <p className="mt-2 text-sm text-ink/85">
                <span className="font-medium text-ink">Produces:</span>{" "}
                {s.produces}
              </p>
              <p className="mt-1 text-sm text-ink/85">
                <span className="font-medium text-ink">Prevents:</span>{" "}
                {s.prevents}
              </p>
            </div>
          ))}
        </div>
      </Section>

      <Section heading="A worked example">
        <p>
          Example numbers, to show the arithmetic rather than to report a real
          account.
        </p>
        <Callout label="Example">
          <p className="text-sm">
            A cycle samples 20 prompts × 4 assistants × 3 runs = 240 answers.
            Your brand appears in 60 of them: 20 as the lead recommendation
            (weight 1.0), 25 as a plain listing (weight 0.6), 15 caveated
            (weight 0.3). Your weighted total is 20 + 15 + 4.5 = 39.5. Scoring
            every brand named in those same 60 answers the same way gives a
            combined weighted total of 158. ASOV = 39.5 ÷ 158 × 100 = 25%.
          </p>
        </Callout>
        <p>
          The same run produces a Share of Model of 25% as well (60 of 240
          answers), and that coincidence is the point of tracking both: when
          they drift apart, the gap tells you whether you are losing
          appearances or losing ground inside the appearances you still get.
        </p>
      </Section>

      <Section heading="What a good ASOV looks like">
        <p>
          There is no universal benchmark, because it depends on how many
          credible vendors exist in your category. Judge it two ways. First,
          against the named competitors scored on the same prompt set: in a
          category where five vendors are plausible answers, an even split is
          roughly 20%. Second, against your own trend on an unchanged prompt
          set, per assistant and per journey stage.
        </p>
        <p>
          Read it stage by stage. A strong Decision-stage ASOV with a weak
          Discovery figure is the common pattern, and it means assistants
          endorse you once a buyer already knows your name but never raise you
          in the earlier question where the shortlist gets formed. For the
          frequency side of the same measurement, see{" "}
          <Link to="/what-is-share-of-model" className="underline">
            what Share of Model is and how to improve it
          </Link>
          .
        </p>
      </Section>

      <Section heading="How do I know if my brand appears in AI search results?">
        <p>
          You know your brand appears in AI search results when ChatGPT, Claude, Gemini, Perplexity
          or Google AI names or cites you in answer to a buyer question that does not include your
          brand name. Ask each assistant three to five of those questions, repeat each one a few
          times, and note whether you are named, recommended or cited, and who is named instead.
        </p>
        <p>
          Doing this by hand works for a first look but gets slow across six assistants. Tools that
          track it on a schedule are compared in{" "}
          <Link to="/best-ai-visibility-tools-for-b2b-marketing-teams" className="underline">
            the best AI visibility tools to track how AI recommends your company
          </Link>
          .
        </p>
        <p>
          <Link to="/check" className="underline font-medium text-ink">
            Run the free check to see if AI names your brand
          </Link>
          .
        </p>
      </Section>

      <Section heading="Check where AI recommends you">
        <p>
          <Link
            to="/check"
            className="inline-block rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground"
          >
            Check where AI recommends you, free
          </Link>
        </p>
      </Section>
    </MarketingPage>
  );
}
